import { X, Calendar, User, BookOpen, AlertCircle } from 'lucide-react';
interface DueTrainingData {
    id: string;
    created_at: string;
    updated_at: string | null;
    user_id: string;
    course_id: string;
    status: string;
    assigned_date: string;
    due_date: string;
    completed_date: string | null;
    org_id: string;
    email_sent: boolean;
    course_year: string;
    course_title: string;
    curriculum_id: string;
    curriculum_name: string;
    users: {
        email: string;
    };
}

interface DueTrainingModalProps {
    isOpen: boolean;
    onClose: () => void;
    dueTrainingData: DueTrainingData[];
}

export function DueTrainingModal({ isOpen, onClose, dueTrainingData }: DueTrainingModalProps) {
    if (!isOpen) return null;

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'completed':
                return 'text-green-600 bg-green-50';
            case 'inProgress':
                return 'text-blue-600 bg-blue-50';
            case 'notStarted':
                return 'text-amber-600 bg-amber-50';
            default:
                return 'text-gray-600 bg-gray-50';
        }
    };

    const getStatusText = (status: string) => {
        switch (status) {
            case 'completed':
                return 'Completed';
            case 'inProgress':
                return 'In Progress';
            case 'notStarted':
                return 'Not Started';
            default:
                return status;
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
                <div className="flex items-center justify-between p-6 border-b border-gray-200">
                    <div className="flex items-center">
                        <AlertCircle className="w-6 h-6 text-amber-500 mr-3" />
                        <h2 className="text-xl font-semibold text-gray-900">Due Training Details</h2>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <X className="w-6 h-6" />
                    </button>
                </div>

                <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
                    {dueTrainingData.length === 0 ? (
                        <div className="text-center py-8">
                            <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">No due training found</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {dueTrainingData.map((training) => (
                                <div key={training.id} className="bg-gray-50 rounded-lg p-6 border border-gray-200">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Course Information */}
                                        <div className="space-y-4">
                                            <div className="flex items-start">
                                                <BookOpen className="w-5 h-5 text-purple-500 mr-3 mt-1" />
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 mb-1">Curriculum</h3>
                                                    <p className="text-gray-700">{training.curriculum_name || 'N/A'}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-start">
                                                <BookOpen className="w-5 h-5 text-blue-500 mr-3 mt-1" />
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 mb-1">Course Title</h3>
                                                    <p className="text-gray-700">{training.course_title}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-start">
                                                <User className="w-5 h-5 text-green-500 mr-3 mt-1" />
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 mb-1">Assigned To</h3>
                                                    <p className="text-gray-700">{training.users.email}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Status and Dates */}
                                        <div className="space-y-4">
                                            <div>
                                                <h3 className="font-semibold text-gray-900 mb-2">Status</h3>
                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(training.status)}`}>
                                                    {getStatusText(training.status)}
                                                </span>
                                            </div>

                                            <div className="flex items-start">
                                                <Calendar className="w-5 h-5 text-purple-500 mr-3 mt-1" />
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 mb-1">Due Date</h3>
                                                    <p className="text-gray-700">{formatDate(training.due_date)}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-start">
                                                <Calendar className="w-5 h-5 text-gray-500 mr-3 mt-1" />
                                                <div>
                                                    <h3 className="font-semibold text-gray-900 mb-1">Assigned Date</h3>
                                                    <p className="text-gray-700">{formatDate(training.assigned_date)}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="flex justify-end p-6 border-t border-gray-200">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
}
